import { useEffect, useState } from 'react';
import { useBrandingStore } from '@/stores/branding';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/stores/auth';
import { isSupplierPortalUser } from '@/lib/supplier-portal-user';
import { api } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import { AuthShell } from './AuthShell';

interface RegisterPageProps {
  onNavigate: (path: string) => void;
}

interface RegistrationStatus {
  registration_enabled: boolean;
  invite_required: boolean;
}

function readInviteFromHash(): string {
  if (typeof window === 'undefined') return '';
  const hash = window.location.hash;
  const query = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
  if (!query) return '';
  const params = new URLSearchParams(query);
  return (params.get('invite') || params.get('code') || '').trim();
}

function mapRegisterError(error?: string): string {
  switch (error) {
    case 'Registration is disabled':
      return 'Die Registrierung ist derzeit deaktiviert. Bitte wenden Sie sich an einen Administrator.';
    case 'Invite code required':
      return 'Ein Einladungscode ist erforderlich.';
    case 'Invalid or expired invite code':
      return 'Der Einladungscode ist ungültig oder abgelaufen.';
    case 'Invite code is for a different email':
      return 'Dieser Einladungscode gilt für eine andere E-Mail-Adresse.';
    case 'Email already registered':
      return 'Diese E-Mail-Adresse ist bereits registriert.';
    case 'Failed to create user':
      return 'Konto konnte nicht erstellt werden. Bitte versuchen Sie es erneut.';
    default:
      return error || 'Registrierung fehlgeschlagen.';
  }
}

export function RegisterPage({ onNavigate }: RegisterPageProps) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState(() => readInviteFromHash());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [status, setStatus] = useState<RegistrationStatus>({
    registration_enabled: true,
    invite_required: false,
  });

  const register = useAuthStore((s) => s.register);

  useEffect(() => {
    trackEvent('signup_view', { surface: 'register_page' });
    void useBrandingStore.getState().load();

    let cancelled = false;
    const loadStatus = async () => {
      try {
        const res = await api.get<{ success?: boolean; data?: RegistrationStatus }>('/auth/registration-status');
        if (!cancelled && res.success && res.data) {
          setStatus({
            registration_enabled: res.data.registration_enabled !== false,
            invite_required: res.data.invite_required === true,
          });
        }
      } catch {
        // Keep permissive defaults; server still enforces settings.
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    };
    void loadStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackEvent('signup_submit', { has_invite_code: Boolean(inviteCode) });
    setError('');

    if (!status.registration_enabled) {
      setError(mapRegisterError('Registration is disabled'));
      return;
    }
    if (status.invite_required && !inviteCode.trim()) {
      setError(mapRegisterError('Invite code required'));
      return;
    }
    if (fullName.trim().length < 2) {
      setError('Bitte geben Sie Ihren vollständigen Namen an.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen haben.');
      return;
    }

    setLoading(true);
    const result = await register(email, password, fullName.trim(), inviteCode.trim() || undefined);
    setLoading(false);
    if (result.success) {
      trackEvent('signup_success', { method: 'password' });
      const roles = useAuthStore.getState().user?.roles as string[] | undefined;
      onNavigate(isSupplierPortalUser(roles) ? '/supplier-portal' : '/dashboard');
      return;
    }
    trackEvent('signup_failed', { method: 'password' });
    setError(mapRegisterError(result.error));
  };

  return (
    <AuthShell
      title="Konto erstellen"
      description={
        status.invite_required
          ? 'Erstellen Sie ein Konto mit Ihrem Einladungscode.'
          : 'Erstellen Sie ein neues Konto.'
      }
    >
      {!statusLoading && !status.registration_enabled ? (
        <div
          role="alert"
          className="rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning"
        >
          Die Registrierung ist derzeit deaktiviert. Bitte wenden Sie sich an einen Administrator.
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div
              role="alert"
              className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger"
            >
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="fullName">Vollständiger Name</Label>
            <Input
              id="fullName"
              type="text"
              placeholder="Max Mustermann"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              minLength={2}
              disabled={loading || statusLoading}
              autoComplete="name"
              autoFocus
              voiceInput={false}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">E-Mail</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@beispiel.ch"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading || statusLoading}
              autoComplete="email"
              voiceInput={false}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inviteCode">
              Einladungscode{status.invite_required ? '' : ' (optional)'}
            </Label>
            <Input
              id="inviteCode"
              type="text"
              placeholder="XXXX-XXXX"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              required={status.invite_required}
              disabled={loading || statusLoading}
              autoComplete="one-time-code"
              voiceInput={false}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Passwort</Label>
            <Input
              id="password"
              type="password"
              placeholder="Mindestens 8 Zeichen"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              disabled={loading || statusLoading}
              autoComplete="new-password"
              voiceInput={false}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              disabled={loading || statusLoading}
              autoComplete="new-password"
              voiceInput={false}
            />
          </div>

          <Button
            type="submit"
            className="btn-interactive w-full"
            disabled={loading || statusLoading}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/70 border-t-transparent" />
                Wird registriert…
              </span>
            ) : (
              'Konto erstellen'
            )}
          </Button>
        </form>
      )}

      <p className="mt-6 text-center text-sm text-muted">
        Bereits ein Konto?{' '}
        <button
          type="button"
          className="font-medium text-foreground underline-offset-4 hover:underline"
          onClick={() => onNavigate('/auth/login')}
        >
          Anmelden
        </button>
      </p>
    </AuthShell>
  );
}
